/**
* Licensed Materials - Property of IBM
* IBM Cognos Products: BI Dashboard
* (C) Copyright IBM Corp. 2021
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/

define([
	'underscore',
	'jquery',
	'../../lib/@waca/dashboard-common/dist/utils/TextEditorConstants'
],function(_, $, TextEditorConstants) {

	const FONT_SIZE_STYLES = ['font-size'];

	class StaticTextHelper {
		constructor({ Colors }) {
			this.Colors = Colors;
			this.colourClassNames = null;
		}

		/**
		 * Determine whether a snippet of HTML contains text sizing markup
		 * @param {string} titleHtml 
		 * @returns `true` when `titleHtml` contains text with hard-coded sizing
		 */
		titleContainsSize(titleHtml) {
			return !!(
				this._extractVisibleTitleLines($(titleHtml))
					.find($lineHtml => this._titleLineContainsSize($lineHtml))
			);
		}

		/*
		 * The full HTML in titleHtml is rather large, most of which isn't the actual rendered title. 
		 * This function extracts the elements of the user-visible title. 
		 * As the user can create a title with multiple lines, each line is treated as a separate element.
		 */
		_extractVisibleTitleLines($titleHtml) {
			return $titleHtml.find('p')
				.toArray()
				.map(lineElement => $(lineElement));
		}

		_titleLineContainsSize($titleLine) {
			return this._getTitleLineSizeAttribute($titleLine).length > 0;
		}

		_getTitleLineSizeAttribute($titleLine) {
			return this._getElementsWithFormatAttributes($titleLine)
				.map(
					({ $e, classNames, style }) => ({
						$e,
						styles: this._getFontSizeStyles(style),
						classNames: this._getFontSizeClassNames(classNames)
					})
				)
				.filter(
					({ classNames, styles }) => classNames.length > 0 || styles.length > 0
				);
		}

		_getElementsWithFormatAttributes($root) {
			return $root.find('span, font').toArray()
				.map(e => $(e))
				.map($e => ({
					$e,
					classNames: $e.attr('class') || '',
					style: $e.attr('style') || ''
				}));
		}

		_getFontSizeStyles(style) {
			return FONT_SIZE_STYLES.filter(styleName => (style.indexOf(styleName) >= 0));
		}

		_getFontSizeClassNames(classNames) {
			const classNameList = classNames.split(/\s+/g);
			const colourClassNames = this._getColourClassNames();
			const fontClassNames = _.pluck(TextEditorConstants.FONT_FAMILY_CHOICES, 'cssClass');

			return _.chain(classNameList)
				.filter(className => className && className.length)
				.difference(colourClassNames, fontClassNames)
				.value();
		}

		_getColourClassNames() {
			if (!this.colourClassNames) {
				const colours = this.Colors.getDashboardColorSet();
				this.colourClassNames = (colours || []).map(({ id }) => id);
				this.colourClassNames.push('responsiveColor');
			}
			return this.colourClassNames;
		}

		/**
		 * Remove any text sizing from a snippet of HTML
		 * @param {string} titleHtml
		 * @returns `titleHtml`, but with any markup which sets text size removed
		 */
		stripTitleOfSize(titleHtml) {
			const $titleHtml = $(titleHtml);

			this._extractVisibleTitleLines($titleHtml)
				.forEach(
					$titleLine => this._getTitleLineSizeAttribute($titleLine)
						.forEach(
							({ $e, styles, classNames }) => this._stripFontSizeFormatting({ $e, styles, classNames })
						)
				);

			return $titleHtml[0].outerHTML;
		}

		_stripFontSizeFormatting({ $e, styles, classNames }) {
			styles.forEach(style => $e.css(style, ''));
			classNames.forEach(className => $e.toggleClass(className, false));
		}
	}

	return StaticTextHelper;
});
